import {
  Component,
  Input,
  TemplateRef,
  ViewChild,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  MatLegacyDialogRef as MatDialogRef,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogConfig as MatDialogConfig
} from '@angular/material/legacy-dialog';
import { NoopScrollStrategy } from '@angular/cdk/overlay';

@Component({
  selector: 'zef-dialog',
  templateUrl: './dialog.component.html',
  styleUrls: [ './dialog.component.scss' ]
})
export class DialogComponent implements OnDestroy {

  dialog: MatDialogRef<any>;
  private _opening = false;
  private _openTimer: ReturnType<typeof setTimeout> | null = null;
  private _destroyed = false;
  afterClosedSubscription: Subscription;
  afterOpenedSubscription: Subscription;
  onEscKeySubscription: Subscription;

  @Input()
  // matches MatDialogConfig#autoFocus: boolean, 'first-tabbable' | 'dialog'
  // | 'first-heading', or a CSS selector of the element to focus
  autoFocus: boolean | string = true;

  @Input()
  options: MatDialogConfig = {};

  @Input()
  backdropClass: string | string[];

  @Input()
  paneClass: string;

  @Output()
  closeTrigger = new EventEmitter<void>();

  @Output()
  afterOpen = new EventEmitter<void>();

  @ViewChild('ref', { static: true })
  templateRef: TemplateRef<any>;

  @Input()
  set open(v: boolean) {
    if (v === true && !this.dialog && !this._opening) {
      this._opening = true;
      this._openTimer = setTimeout(() => {
        this._openTimer = null;
        this._opening = false;
        // Host was destroyed while the open was still pending — don't spawn an
        // overlay no live component owns (it would orphan in the DOM).
        if (this._destroyed) { return; }
        this.dialog = this._dialog.open(this.templateRef, {
          disableClose: true,
          ...this.options,
          autoFocus: this.autoFocus,
          backdropClass: this.backdropClass,
          panelClass: this.paneClass,
          restoreFocus: false,
          scrollStrategy: new NoopScrollStrategy()
        });

        this.afterClosedSubscription = this.dialog.afterClosed().subscribe(() => {
          this.closeTrigger.emit();
        });

        this.afterOpenedSubscription = this.dialog.afterOpened().subscribe(() => {
          this.afterOpen.emit();
        });

        this.onEscKeySubscription = this.dialog.keydownEvents().subscribe((event) => {
          if (event.key === 'Escape') {
            if (this.dialog && (this.options.disableClose === false || !this.options.disableClose)) {
              this._closeDialog();
              this.closeTrigger.emit();
            }
          }
        });
      });
    } else if (!v && this._openTimer) {
      // closed again while the deferred open was still pending — cancel the
      // timer, otherwise the dialog opens against a closed store state and
      // every subsequent toggle does the opposite of what's expected
      clearTimeout(this._openTimer);
      this._openTimer = null;
      this._opening = false;
    } else if (this.dialog) {
      this._closeDialog();
    }
  }

  constructor(private _dialog: MatDialog) {}

  ngOnDestroy() {
    // The MatDialog overlay attaches to the CDK overlay container (document
    // body), NOT inside this component's view — so if the host is destroyed
    // while the dialog is open (e.g. a route-scoped page navigates away
    // mid-flow), the overlay orphans in the DOM, uncloseable. Tear it down on
    // destroy, and cancel any pending open so a late timer can't spawn one.
    this._destroyed = true;
    if (this._openTimer) {
      clearTimeout(this._openTimer);
      this._openTimer = null;
    }
    if (this.dialog) {
      this._closeDialog();
    }
  }

  close() {
    this.dialog?.close();
  }

  private _closeDialog() {
    this.dialog.close();
    this.dialog = null;
    this.afterClosedSubscription.unsubscribe();
    this.afterOpenedSubscription.unsubscribe();
    this.onEscKeySubscription.unsubscribe();
  }

}
